import { get, last, map } from 'lodash';
import moment from 'moment';
import languageNameMap from 'language-name-map/map';

import { sanitizeUrl } from '@braintree/sanitize-url';

import { ACTIVITY_CATEGORY_OPTIONS, ACTIVITY_CATEGORY_COLORS } from './constants';

/**
 * Parse file name from the given url of an uploaded file.
 * @param {string} url File url.
 * @param {boolean} removeTimestamp Wheather or not to remove the timestamp from start of the file name.
 * @returns Parsed file name.
 */
export function parseFileNameFromUrl(url, removeTimestamp = false) {
  let fileName = decodeURI(last(url.split('/')));
  if (removeTimestamp) fileName = fileName.replace(/^[0-9.]+_/, '');
  return fileName;
}

export function formatFullDate(date) {
  return moment(date).format('MMMM DD, YYYY');
}

// Installment due dates arrive JSON-quoted and may be a plain date ("2026-03-30") or an
// offset-aware datetime ("2026-04-01T23:59:59-04:00"). Strip the quotes; the datetime form
// is rendered in the viewer's own timezone (same instant, local clock), so a student sees
// the deadline in their local time.
export function cleanPlanDate(date) {
  return String(date == null ? '' : date).replace(/^"+|"+$/g, '').trim();
}
export function formatInstallmentDate(date) {
  const cleaned = cleanPlanDate(date);
  if (!cleaned) return '';
  const parsed = moment(cleaned);
  if (!parsed.isValid()) return '';
  // Show the time only when the value carries one (the offset-datetime form); legacy
  // date-only values have no meaningful time, so render just the day.
  const hasTime = cleaned.includes('T');
  return parsed.format(hasTime ? 'MMMM DD, YYYY, hh:mm A' : 'MMMM DD, YYYY');
}
// Calendar-day sort key that orders mixed date-only and offset-datetime installments
// chronologically (uses the value's own offset so ordering is stable across viewers).
export function planDateSortKey(date) {
  const cleaned = cleanPlanDate(date);
  const parsed = moment.parseZone(cleaned);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
}
// True when an installment's due moment has already passed. Datetime values carry their
// own end-of-day time; legacy date-only values are treated as end of that day.
export function isInstallmentOverdue(date) {
  const cleaned = cleanPlanDate(date);
  if (!cleaned) return false;
  const due = cleaned.includes('T') ? moment(cleaned) : moment(cleaned).endOf('day');
  return due.isValid() && due.isBefore(moment());
}
export function formatShortDate(date) {
  return moment(date).format('MMM DD, YYYY');
}
export function formatDateTime(dateTime) {
  return moment(dateTime).format('MMM DD, YYYY, hh:mm A');
}
// Exact backend UTC timestamp with no timezone conversion (e.g. payout created_at).
export function formatUtcExact(dateTime) {
  return moment.utc(dateTime).format('MMM DD, YYYY, hh:mm A [UTC]');
}
export function formatDocsDateTime(dateTime) {
  return moment(dateTime).format('MM/DD/YY  hh:mm a');
}
export function formatTimeAgo(dateTime) {
  return moment(dateTime).fromNow();
}
export function formatTime(dateTime) {
  return moment(dateTime).fromNow(true);
}
export function formatDateSimple(date) {
  return moment(date).format('MM/DD/YY');
}
export function formatToAPIDate(date) {
  return moment(date).format('YYYY-MM-DD');
}

export function isDateExpired(expiresAt) {
  return moment(expiresAt).isSameOrBefore(formatToAPIDate(moment()));
}

export function getLangOptions(nameKey = 'name') {
  return Object.keys(languageNameMap).map(key => ({ value: key, [nameKey]: languageNameMap[key].name }));
}

export function getClickableLink(link) {
  const protocol = process.env.NODE_ENV === 'development' ? 'http' : 'https';
  return link.startsWith('http://') || link.startsWith('https://')
    ? sanitizeUrl(link)
    : sanitizeUrl(`${protocol}://${link}`);
}

export function getClickableLinkUsSanitize(link) {
  return link.startsWith('http://') || link.startsWith('https://') ? link : '';
}

export const NOTIFICATION_EXTRA_MAPPINGS = (action, notification) => {
  return {
    emp_scl_partnership_requested: [notification.extra.request_from_name],
    emp_scl_partnership_accepted: [notification.extra.request_from_name],
    testimonial_posted: [get(notification, 'notifier.first_name') + ' ' + get(notification, 'notifier.last_name')],
    signup_request_posted: [get(notification, 'notifier.first_name') + ' ' + get(notification, 'notifier.last_name')],
    partnership_rec_fee_changed: [notification.extra.school_name, notification.extra.new_rec_fee],
    partnership_rec_fee_changed_free: [notification.extra.school_name],
    partnership_ref_fee_changed: [notification.extra.school_name, notification.extra.new_ref_fee],
    successfully_hired: [notification.extra.employer_name],
    token_purchased: [notification.extra.tokens],
    std_profile_unlocked: [notification.extra.employer_name],
    org_std_profile_unlocked: [notification.extra.org_name],
    user_commented: [
      get(notification, 'notifier.first_name') + ' ' + get(notification, 'notifier.last_name'),
      get(notification, 'extra.comment_on_type', 'skill'),
    ],
    user_replied: [
      get(notification, 'notifier.first_name') + ' ' + get(notification, 'notifier.last_name'),
      get(notification, 'extra.comment_on_type', 'skill'),
    ],
    feedback_posted: [get(notification, 'notifier.first_name') + ' ' + get(notification, 'notifier.last_name')],
    feedback_replied: [get(notification, 'notifier.first_name') + ' ' + get(notification, 'notifier.last_name')],
    status_approved: [get(notification, 'notifier.first_name') + ' ' + get(notification, 'notifier.last_name')],
    post_liked: [get(notification, 'notifier.first_name') + ' ' + get(notification, 'notifier.last_name')],
    school_credited: [
      get(notification, 'extra.credits_earned', 1),
      get(notification, 'extra.student_name', ''),
      get(notification, 'extra.employer_name', 'an employer'),
    ],
    org_credits_deduct: [get(notification, 'extra.employer_name'), get(notification, 'extra.credits_used')],
    org_token_deduct: [get(notification, 'extra.tokens_used'), get(notification, 'extra.employer_name')],
    signup_org_employer: [notification.extra.employer_name],
    app_feature_update: [notification.extra.notification_title],
    new_signup_request: [notification.extra.school_name],
    school_demo_request: [notification.extra.school_name],
    simulation_submission: [notification.extra.student_name, notification.extra.simualtion_name],
    ai_skill_card_generation: [get(notification, 'extra.skill_card_id')],
    std_ai_skill_card_generated: [get(notification, 'extra.skill_card_id')],
    ai_skill_card_approved: [get(notification, 'extra.skill_card_id')],
    participants_details_report: [get(notification, 'extra.file_url')],
    graded_assignment_submitted: [
      get(notification, 'extra.student_name'),
      get(notification, 'extra.assignment_title'),
      get(notification, 'extra.school_name', ''),
    ],
    ai_assignment_graded: [
      get(notification, 'extra.student_name'),
      get(notification, 'extra.assignment_title'),
      Number.isFinite(Number(get(notification, 'extra.ai_percentage')))
        ? Number(get(notification, 'extra.ai_percentage')).toFixed(2)
        : '0.00',
    ],
    assignment_graded: [
      get(notification, 'extra.assignment_title'),
      get(notification, 'extra.score_obtained'),
      get(notification, 'extra.total_marks'),
      get(notification, 'extra.percentage'),
    ],
    student_pii_rejected: [
      get(notification, 'extra.pii_type', '').replace(/_/g, ' '),
      get(notification, 'extra.reason', ''),
    ],
    student_pii_reuploaded: [
      get(notification, 'extra.student_name', ''),
      get(notification, 'extra.pii_types', []).map(type => type.replace(/_/g, ' ')).join(', '),
    ],
    email_reply_received: [
      get(notification, 'extra.sender_name', get(notification, 'extra.sender_email', '')),
      get(notification, 'extra.subject', ''),
    ],
    sms_reply_received: [
      get(notification, 'extra.from_phone', ''),
      get(notification, 'extra.preview', ''),
    ],
    SMS_REPLY_RECEIVED: [
      get(notification, 'extra.from_phone', ''),
      get(notification, 'extra.preview', ''),
    ],
    sms: [
      get(notification, 'extra.from_phone', ''),
      get(notification, 'extra.preview', ''),
    ],
  }[action];
};

export function titleize(value) {
  return value.toLowerCase().replace(/(?:^|\s|-)\S/g, x => x.toUpperCase());
}

export function getHumanReadableCommaSepStr(arr, lastSep = 'and') {
  return [arr.slice(0, -1).join(', '), arr.slice(-1)[0]].join(arr.length < 2 ? '' : ` ${lastSep} `);
}
export function capitalizeFirstLetter(word) {
  return word.charAt(0).toUpperCase() + word.slice(1);
}
export function hexToRgb(hex) {
  // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
  var shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  hex = hex.replace(shorthandRegex, function(m, r, g, b) {
    return r + r + g + g + b + b;
  });

  var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

// export function stripHtmlTags(value) {
//   const noTags = (value || '').replace(/(<([^>]+)>)/gi, '');
//   return (noTags + '').replace(/&#\d+;/gm, s => String.fromCharCode((s.match(/\d+/gm) || [])[0]));
// }

export function stripHtmlTags(value) {
  const noTags = (value || '').replace(/(<([^>]+)>)/gi, '');
  return (noTags + '')
    .replaceAll('&nbsp;', '')
    .replace(/&#\d+;/gm, s => String.fromCharCode((s.match(/\d+/gm) || [])[0]));
}

export function getWorkTravelOptions() {
  const options = map([...Array(9).keys()], num => ({
    value: `${(num + 1) * 10}`,
    text: `${(num + 1) * 10} miles`,
  }));
  options.push({ value: '100+', text: '100+ miles' });
  return options;
}

export function computeAddress(data, keys, stateKey = 'state', zipcodeKey = 'zipcode') {
  let address = '';
  keys.forEach(item => {
    address = address
      .concat(
        item === zipcodeKey && data[stateKey] && data[zipcodeKey] ? ' ' : data[item] && address !== '' ? `, ` : '',
      )
      .concat(data[item] ? data[item] : '');
  });
  return address;
}

export function dataURLToBlob(dataURL) {
  const byteString = atob(dataURL.split(',')[1]);
  const mimeString = dataURL
    .split(',')[0]
    .split(':')[1]
    .split(';')[0];
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new Blob([ab], { type: mimeString });
}

/**
 * Convert minutes to hours and minutes format
 * @param {number} minutes Total minutes
 * @returns {string} Formatted string like "2h 30min" or "45min"
 */
export function formatMinutesToHoursAndMinutes(minutes) {
  if (!minutes || minutes < 0) return 'N/A';
  
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  
  if (hours === 0) {
    return `${remainingMinutes}min`;
  } else if (remainingMinutes === 0) {
    return `${hours}h`;
  } else {
    return `${hours}h ${remainingMinutes}min`;
  }
}

// Fixed, theme-independent colors for activity categories so 'general' never
// collides with the school's brand palette (badge/bg 'secondary' is remapped
// to the brand --secondary-color, which can be red like 'danger').
export function activityCategoryStyle(category) {
  const background = ACTIVITY_CATEGORY_COLORS[category] || ACTIVITY_CATEGORY_COLORS.general;
  return `background-color: ${background}; color: #fff;`;
}

export function activityCategoryLabel(category) {
  const option = ACTIVITY_CATEGORY_OPTIONS.find(o => o.value === category);
  return option ? option.text : 'General';
}

export function activityCategoryColor(category) {
  return ACTIVITY_CATEGORY_COLORS[category] || ACTIVITY_CATEGORY_COLORS.general;
}
